import { Box, Button, Flex, HStack, Text } from "@chakra-ui/react";
import { CalendarDate } from "@internationalized/date";
import CalendarIconButton from "@raiden/library/components/DatePicker/CalendarIconButton";
import MonthSelector from "@raiden/library/components/DatePicker/MonthSelector";
import YearSelector from "@raiden/library/components/DatePicker/YearSelector";
import { useCalendar } from "@react-aria/calendar";
import dayjs from "dayjs";
import { useRef } from "react";
import { FormattedMessage } from "react-intl";
import { Icon } from "../Icon";
import { CalendarGrid } from "./CalendarGrid";

/**
 * @typedef Props
 * @property {import("@raiden/library/types/RentalArea").RentalArea} rentalArea
 * @property {import("@raiden/library/types/common/DatePicker").UnavailableRanges[]} unavailableRanges
 * @property {import("@react-stately/calendar").CalendarState} localState
 * @property {import("@react-stately/datepicker").DatePickerState} state
 * @property {(event: any) => void} onChange
 * @property {() => void} [onClick]
 */
/**
 * @param {Props} props
 */
export function Calendar(props) {
  const { rentalArea, unavailableRanges, onChange, onClick, localState } =
    props;

  let ref = useRef(null);
  let { calendarProps, prevButtonProps, nextButtonProps } = useCalendar(
    props,
    localState,
  );
  function setDateToToday() {
    const today = dayjs();
    const date = new CalendarDate(
      today.year(),
      today.month() + 1,
      today.date(),
    );
    onChange(date);
    localState.setFocusedDate(date);
  }

  return (
    <Box {...calendarProps} ref={ref}>
      <Flex gap=".75rem" mb=".5rem" wrap="wrap">
        <CalendarIconButton
          {...prevButtonProps}
          bg="none"
          icon={<Icon icon="ms_arrow_back" color="gray.400" size="1.25rem" />}
          state={localState}
          label="previous"
          aria-label="Previous month"
          isDisabled={dayjs().isAfter(
            dayjs(localState.visibleRange.start.toString()),
          )}
          _hover={{ bg: "none" }}
        />

        <CalendarIconButton
          {...nextButtonProps}
          bg="none"
          icon={
            <Icon icon="ms_arrow_forward" color="gray.400" size="1.25rem" />
          }
          state={localState}
          label="next"
          aria-label="Next month"
          _hover={{ bg: "none" }}
        />

        <Button
          variant="outline"
          borderRadius="6px"
          size="sm"
          onClick={setDateToToday}>
          <Text fontWeight={600} fontSize=".75rem">
            <FormattedMessage defaultMessage="Aujourd'hui" />
          </Text>
        </Button>

        <HStack spacing=".5rem" px=".5rem">
          <MonthSelector state={localState} borderRadius="6px" />

          <YearSelector state={localState} borderRadius="6px" />
        </HStack>
      </Flex>

      <CalendarGrid
        rentalArea={rentalArea}
        unavailableRanges={unavailableRanges}
        state={localState}
        onClick={onClick}
      />
    </Box>
  );
}
